import { createTheme, type PaletteMode, type Theme } from "@mui/material/styles";
import { DARK, LIGHT, type Tokens } from "./tokens";

declare module "@mui/material/styles" {
  interface Palette {
    brand: { link: string; accentSoft: string; surface2: string };
  }
  interface PaletteOptions {
    brand?: { link: string; accentSoft: string; surface2: string };
  }
}

export const DISPLAY_FONT = '"Space Grotesk", "Helvetica Neue", Arial, sans-serif';

/**
 * Light (scheme A, paper / ink / amber) is the console default; dark (scheme
 * B, ink / paper / amber) is the toggle and the marketing pages. One token
 * set feeds both, so nothing is designed twice.
 */
export function makeTheme(mode: PaletteMode): Theme {
  const t: Tokens = mode === "dark" ? DARK : LIGHT;
  return createTheme({
    palette: {
      mode,
      primary: { main: t.primary, dark: t.primaryHover, light: t.primaryHover, contrastText: t.onPrimary },
      secondary: { main: t.accent, dark: t.accentHover, light: t.accentHover, contrastText: t.onAccent },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.text, secondary: t.text2, disabled: t.text3 },
      divider: t.border,
      success: { main: t.success, contrastText: mode === "dark" ? t.bg : t.onPrimary },
      warning: { main: t.warning, contrastText: mode === "dark" ? t.bg : t.onPrimary },
      error: { main: t.error, contrastText: mode === "dark" ? t.bg : t.onPrimary },
      info: { main: t.info, contrastText: mode === "dark" ? t.bg : t.onPrimary },
      brand: { link: t.link, accentSoft: t.accentSoft, surface2: t.surface2 },
    },
    shape: { borderRadius: 6 },
    typography: {
      h1: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      h2: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      h3: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      h4: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      h5: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      h6: { fontFamily: DISPLAY_FONT, fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: t.bg } },
      },
      // 🪤 MUI draws a checked switch in `primary` at 50% opacity. Our primary
      // is the ink (navy in light, near-white in dark), so ON and OFF both read
      // as grey -- the owner could not tell whether "Send me the weekly
      // summary" was on (2026-09-28). ON is therefore a solid green track and
      // OFF a solid grey one, in both modes. Green, not amber: amber is kept
      // for the one call to action on a screen (see tokens.ts).
      // Contrast against the card (WCAG non-text, 3:1): ON 5.67 light / 6.58
      // dark; OFF track 3.06 / 4.01; the checked thumb against green 5.67 /
      // 6.58 (a white thumb on the dark-mode green is only 2.19, hence ink).
      MuiSwitch: {
        styleOverrides: {
          track: { backgroundColor: t.text3, opacity: 1 },
          switchBase: {
            "&.Mui-checked": { color: mode === "dark" ? t.surface : "#FFFFFF" },
            "&.Mui-checked + .MuiSwitch-track": { backgroundColor: t.success, opacity: 1 },
            "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
          },
        },
      },
      MuiAppBar: {
        defaultProps: { color: "default", elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            color: t.text,
            borderBottom: `1px solid ${t.border}`,
            backgroundImage: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: t.surface2, borderRight: `1px solid ${t.border}`, backgroundImage: "none" },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: "2px 8px",
            "&.Mui-selected": { backgroundColor: t.accentSoft, fontWeight: 600 },
            "&.Mui-selected:hover": { backgroundColor: t.accentSoft },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedSecondary: { "&:hover": { backgroundColor: t.accentHover } },
          outlined: { borderColor: t.borderStrong },
        },
      },
      MuiCard: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiLink: {
        styleOverrides: { root: { color: t.link } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
      MuiTableHead: {
        styleOverrides: { root: { backgroundColor: t.surface2 } },
      },
    },
  });
}

export const lightTheme = makeTheme("light");
export const darkTheme = makeTheme("dark");
